import React from "react";
import { LocalizedLink as Link } from "../components/LocalizedLink";
import {
  ArrowRight,
  BadgePercent,
  BatteryCharging,
  Brain,
  Building2,
  CalendarCheck,
  CheckCircle2,
  ClipboardCheck,
  HeartPulse,
  MapPin,
  Phone,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  UsersRound,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import nutricImage from "../assets/iv/nutric.webp";
import { isContentEditable } from "@testing-library/user-event/dist/utils";

const CONTENT = {
  es: {
    eyebrow: "Corporate Wellness Experience",
    title: "Nutri C IV para empresas",
    intro:
      "Lleva bienestar clínico directamente a tu oficina con un programa onsite para equipos de 15 personas o más.",
    description:
      "Las grandes empresas invierten en lo más valioso: Su equipo. Nutri C ayuda a elevar energía, enfoque y bienestar para jornadas de alto rendimiento.",
    primaryCta: "Solicitar paquete empresarial",
    secondaryCta: "Agendar fecha",
    whatsappMessage:
      "Hola, quiero información del Paquete Empresarial Nutri C para 15 personas o más.",
    stats: [
      { value: "15+", label: "colaboradores" },
      { value: "10%", label: "descuento empresarial" },
      { value: "Onsite", label: "en tu empresa" },
    ],
    highlights: [
      "Paquete exclusivo para grupos corporativos.",
      "Servicio coordinado en tus instalaciones.",
      "Aplican condiciones y valoración clínica previa.",
    ],
    benefitsTitle: "Beneficios para tu equipo",
    benefitsIntro:
      "Una experiencia de bienestar diseñada para reducir desgaste, sostener la energía y reforzar el compromiso interno.",
    benefits: [
      {
        title: "Mejor rendimiento",
        text: "Nutri C combina vitamina C, complejo B y micronutrientes para apoyar las defensas del organismo.",
        icon: ShieldCheck,
        accent: "from-cyan-500 to-sky-500",
      },
      {
        title: "Más energía laboral",
        text: "Acompaña jornadas demandantes con hidratación, vitaminas y soporte para el desempeño diario.",
        icon: BatteryCharging,
        accent: "from-amber-400 to-orange-500",
      },
      {
        title: "Mayor productividad",
        text: "Ideal para equipos con cargas altas, estrés operativo o periodos de trabajo intensivo.",
        icon: HeartPulse,
        accent: "from-emerald-500 to-teal-500",
      },
      {
        title: "Mayor Enfoque",
        text: "El complejo B contribuye al metabolismo energético y al funcionamiento normal del sistema nervioso.",
        icon: Brain,
        accent: "from-slate-600 to-cyan-700",
      },
    ],
    packageTitle: "Inversión corporativa",
    packageSubtitle: "Paquete Empresarial Nutri C",
    regularLabel: "Precio regular",
    corporateLabel: "Precio especial empresarial total x 15 personas",
    regularPrice: "$1,770 USD",
    corporatePrice: "$1,593 USD",
    discount: "10% OFF",
    travelTitle: "Servicio en tu empresa",
    travelText: "+$50 USD por traslado de enfermera.",
    priceNote:
      "Calculado para 15 participantes. Si tu equipo es mayor, ajustamos la logística y el presupuesto.",
    processTitle: "Cómo funciona",
    process: [
      {
        title: "Planeación",
        text: "Definimos número de participantes, fecha, horarios y espacio disponible.",
      },
      {
        title: "Coordinación clínica",
        text: "Confirmamos indicaciones, contraindicaciones y requisitos previos.",
      },
      {
        title: "Aplicación onsite",
        text: "Nuestro equipo acude a tu empresa para realizar el servicio de forma organizada.",
      },
      {
        title: "Cierre",
        text: "Terminamos con control de asistencia, recomendaciones generales y seguimiento.",
      },
    ],
    initialTitle: "Servicio para empresas",
    initialText: "Una empresa saludable proyecta liderazgo",
    initialDescription: "El bienestar de tu equipo es un factor clave para el rendimiento de tu empresa. Integra el cuidado integral de tus colaboradores como un pilar estratégico de tu cultura organizacional."
  },
  en: {
    eyebrow: "Corporate Wellness Experience",
    title: "Nutri C IV for companies",
    intro:
      "Bring clinical wellness directly to your workplace with an onsite program for teams of 15 people or more.",
    description:
      "Leading companies invest in their most valuable asset: Their team. Nutri C supports energy, focus and wellness for high-performance workdays.",
    primaryCta: "Request corporate package",
    secondaryCta: "Book a date",
    whatsappMessage:
      "Hi, I would like information about the Nutri C corporate package for 15 people or more.",
    stats: [
      { value: "15+", label: "team members" },
      { value: "10%", label: "corporate discount" },
      { value: "Onsite", label: "at your company" },
    ],
    highlights: [
      "Exclusive package for corporate groups.",
      "Service coordinated at your facilities.",
      "Conditions and prior clinical assessment apply.",
    ],
    benefitsTitle: "Benefits for your team",
    benefitsIntro:
      "A wellness experience designed to reduce wear, sustain energy and reinforce internal commitment.",
    benefits: [
      {
        title: "More Work performance",
        text: "Nutri C combines vitamin C, B complex and micronutrients to support the body's defenses.",
        icon: ShieldCheck,
        accent: "from-cyan-500 to-sky-500",
      },
      {
        title: "More work energy",
        text: "Support demanding schedules with hydration, vitamins and daily performance support.",
        icon: BatteryCharging,
        accent: "from-amber-400 to-orange-500",
      },
      {
        title: "More productivity",
        text: "Ideal for teams with heavy workloads, operational stress or intensive work periods.",
        icon: HeartPulse,
        accent: "from-emerald-500 to-teal-500",
      },
      {
        title: "More Focus",
        text: "B complex contributes to energy metabolism and normal nervous system function.",
        icon: Brain,
        accent: "from-slate-600 to-cyan-700",
      },
    ],
    packageTitle: "Corporate investment",
    packageSubtitle: "Nutri C Corporate Package",
    regularLabel: "Regular price",
    corporateLabel: "Special corporate price",
    regularPrice: "$1,770 USD",
    corporatePrice: "$1,593 USD",
    discount: "10% OFF",
    travelTitle: "Service at your company",
    travelText: "+$50 USD nurse transportation fee.",
    priceNote:
      "Calculated for 15 participants. For larger teams, we adjust logistics and pricing.",
    processTitle: "How it works",
    process: [
      {
        title: "Planning",
        text: "We define participant count, date, schedule and available space.",
      },
      {
        title: "Clinical coordination",
        text: "We confirm indications, contraindications and previous requirements.",
      },
      {
        title: "Onsite application",
        text: "Our team visits your company to provide the service in an organized way.",
      },
      {
        title: "Wrap-up",
        text: "We close with attendance control, general recommendations and follow-up.",
      },
    ],
    initialTitle: "Corporate Wellness Services",
    initialText: "A healthy organization inspires strong leadership",
    initialDescription: "Your team’s well‑being is a key driver of your company’s performance. Make comprehensive employee care a strategic pillar of your organizational culture."
  },
};

export default function Empresas() {
  const { lang } = useLanguage();
  const content = CONTENT[lang] || CONTENT.es;
  const whatsappUrl = `https://wa.me/5216641954918?text=${encodeURIComponent(
    content.whatsappMessage
  )}`;

  return (
    <section id="empresas" className="scroll-mt-24 py-8 md:py-12">
      <div className="container mx-auto px-4 lg:px-8 space-y-12">
        <section className="relative overflow-hidden rounded-2xl bg-white/85 px-6 py-8 text-white shadow-elevated md:px-10 md:py-10">

          <div className="relative z-10 grid items-center gap-8 lg:grid-cols-[1fr_auto]">
            <div>
              <div className="inline-flex w-fit items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-4 py-4 text-sm font-semibold text-turquoise shadow-sm">
                <Building2 className="h-4 w-4" />
                {content.initialTitle}
              </div>
              <h2 className="mt-6 text-3xl font-extrabold text-slate-700">
                {content.initialText}
              </h2>
              <p className="mt-3 max-w-3xl text-slate-600">
                {content.initialDescription}
              </p>
            </div>
          </div>
        </section>

        <div className="overflow-hidden rounded-2xl border border-white/70 bg-white/85 shadow-elevated backdrop-blur-sm">
          <div className="grid lg:grid-cols-[1.08fr_0.92fr]">
            <div className="p-6 sm:p-8 lg:p-12 flex flex-col justify-center">

              <h1 className="mt-6 text-3xl font-extrabold leading-tight text-slate-900 sm:text-4xl lg:text-5xl">
                {content.title}
              </h1>

              <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-700">
                {content.intro}
              </p>

              <p className="mt-4 max-w-2xl text-slate-600">
                {content.description}
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                {content.stats.map((stat, idx) => {
                  const icons = [TrendingUp, Users, MapPin];
                  const Icon = icons[idx] || TrendingUp;
                  const colors = ['from-cyan-500 to-sky-500', 'from-amber-400 to-orange-500', 'from-emerald-500 to-teal-500'];
                  return (
                    <div
                      key={stat.label}
                      className="group rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-soft transition-all duration-300 hover:shadow-elevated hover:-translate-y-0.5"
                    >
                      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${colors[idx]} text-white shadow-soft`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <p className="text-2xl font-extrabold text-slate-900">
                        {stat.value}
                      </p>
                      <p className="mt-1 text-sm font-medium text-slate-600">
                        {stat.label}
                      </p>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn--primary btn--shadow px-6 py-3"
                >
                  <Phone className="h-5 w-5" />
                  {content.primaryCta}
                </a>

                <Link
                  to="/reservar"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-200 bg-white px-6 py-3 font-semibold text-turquoise transition hover:bg-cyan-50"
                >
                  <CalendarCheck className="h-5 w-5" />
                  {content.secondaryCta}
                </Link>
              </div>
            </div>

            <div className="relative min-h-[480px] bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 p-6 sm:p-8 lg:p-10">
              <div className="relative z-10 flex h-full flex-col justify-between gap-8">
                <div className="rounded-2xl bg-white/95 p-4 shadow-elevated">
                  <img
                    src={nutricImage}
                    alt="Nutri C IV"
                    className="mx-auto h-[360px] w-full max-w-sm object-contain"
                    loading="eager"
                  />
                </div>

                <div className="grid gap-3">
                  {content.highlights.map((highlight) => (
                    <div
                      key={highlight}
                      className="flex items-start gap-3 rounded-xl border border-white/15 bg-white/10 p-4 text-white backdrop-blur-sm"
                    >
                      <CheckCircle2 className="mt-1 h-5 w-5 flex-shrink-0 text-cyan-200" />
                      <span className="text-sm sm:text-base">{highlight}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="space-y-6">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-100 text-turquoise">
              <Sparkles className="h-6 w-6" />
            </div>
            <h2 className="text-3xl font-extrabold text-slate-900">
              {content.benefitsTitle}
            </h2>
            <p className="mt-3 text-slate-600">{content.benefitsIntro}</p>
          </div>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {content.benefits.map((benefit) => (
              <article
                key={benefit.title}
                className="group rounded-2xl border border-white/70 bg-white/85 p-6 shadow-soft transition-all duration-300 hover:-translate-y-2 hover:shadow-elevated hover:border-opacity-100"
              >
                <div
                  className={`mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${benefit.accent} text-white shadow-soft transition-all duration-300 group-hover:shadow-lg group-hover:scale-110`}
                >
                  <benefit.icon className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-extrabold text-slate-900">
                  {benefit.title}
                </h3>
                <p className="mt-3 text-slate-600">{benefit.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="rounded-2xl border border-white/70 bg-white/85 p-6 shadow-soft md:p-8">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-100 text-orange-600">
                <BadgePercent className="h-6 w-6" />
              </div>
              <div>
                <p className="font-semibold text-turquoise">
                  {content.packageSubtitle}
                </p>
                <h2 className="text-3xl font-extrabold text-slate-900">
                  {content.packageTitle}
                </h2>
              </div>
            </div>

            <div className="mt-8 grid gap-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                <p className="text-sm font-semibold text-slate-500">
                  {content.regularLabel}
                </p>
                <p className="mt-2 text-3xl font-extrabold text-slate-500 line-through">
                  {content.regularPrice}
                </p>
              </div>

              <div className="relative rounded-xl border-2 border-cyan-400 bg-gradient-to-br from-cyan-50 to-blue-50 p-6 shadow-elevated">
                <div className="absolute -top-3 right-4">
                  <span className="rounded-full bg-gradient-to-r from-orange-500 to-red-500 px-4 py-1.5 text-sm font-bold text-white shadow-lg">
                    🔥 {content.discount}
                  </span>
                </div>
                <div className="flex flex-col gap-3">
                  <p className="text-sm font-semibold text-turquoise">
                    {content.corporateLabel}
                  </p>
                  <p className="text-5xl font-extrabold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                    {content.corporatePrice}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <MapPin className="mt-1 h-5 w-5 flex-shrink-0 text-emerald-600" />
              <div>
                <h3 className="font-extrabold text-slate-900">
                  {content.travelTitle}
                </h3>
                <p className="text-slate-600">{content.travelText}</p>
              </div>
            </div>

            <p className="mt-5 text-sm text-slate-500">{content.priceNote}</p>
          </div>

          <div className="rounded-2xl border border-white/70 bg-white/85 p-6 shadow-soft md:p-8">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                <ClipboardCheck className="h-6 w-6" />
              </div>
              <h2 className="text-3xl font-extrabold text-slate-900">
                {content.processTitle}
              </h2>
            </div>

            <div className="relative mt-8 grid gap-4 md:grid-cols-2">
              {/* Conectores visuales solo en desktop */}
              <div className="absolute inset-0 hidden md:block">
                <div className="absolute top-8 left-[25%] right-[25%] h-1 bg-gradient-to-r from-transparent via-cyan-300 to-transparent"></div>
                <div className="absolute top-8 bottom-8 left-[50%] w-1 bg-gradient-to-b from-cyan-300 to-transparent -translate-x-0.5"></div>
              </div>
              {content.process.map((step, index) => (
                <div
                  key={step.title}
                  className="relative rounded-xl border border-slate-200 bg-white p-5 transition-all duration-300 hover:shadow-elevated hover:border-cyan-300"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-turquoise to-cyan-600 text-base font-extrabold text-white shadow-soft">
                      {index + 1}
                    </span>
                    <h3 className="text-lg font-extrabold text-slate-900">
                      {step.title}
                    </h3>
                  </div>
                  <p className="mt-3 text-slate-600">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>


      </div>
    </section>
  );
}
